"use client";
import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useSession } from "next-auth/react";
import {
  displayReferral,
  publicPage,
  REFERRAL_PATTERN,
  referrerHostname,
  referralCodeFromQuery,
} from "./policy";
import { browserTracking } from "./browser-tracking";

export default function PublicAnalytics() {
  const pathname = usePathname();
  const search = useSearchParams();
  const { data: session, status } = useSession();
  const first = useRef(true);
  const release = useRef<(() => void) | null>(null);
  const ownCode = useRef<string | null>(null);
  const previousUser = useRef<string | null>(null);
  const retiredCodes = useRef(new Set<string>());
  // Snapshot before authentication-dependent address-bar replacements.
  useEffect(() => {
    const initial = first.current;
    first.current = false;
    if (!publicPage(window.location.pathname)) return;
    const navigation = performance.getEntriesByType("navigation")[0] as
      PerformanceNavigationTiming | undefined;
    const kind = initial
      ? navigation?.type === "reload"
        ? "reload"
        : navigation?.type === "back_forward"
          ? "history"
          : "arrival"
      : "internal";
    const host = initial ? referrerHostname(document.referrer) : null;
    release.current = browserTracking().openPage(
      window.location.pathname + window.location.search,
      kind,
      host ? `https://${host}` : null,
    );
    return () => {
      release.current?.();
      release.current = null;
    };
  }, [pathname, search]);
  useEffect(() => {
    if (status === "loading") return;
    const userId = session?.user?.id || null;
    let cancelled = false;
    const rewrite = (code: string | null) => {
      if (cancelled || !publicPage(window.location.pathname)) return;
      const current = new URL(window.location.href);
      const incoming = referralCodeFromQuery(current.searchParams);
      const guestCode =
        incoming &&
        !retiredCodes.current.has(incoming) &&
        REFERRAL_PATTERN.test(incoming)
          ? incoming
          : null;
      const next = displayReferral(
        current.href,
        userId ? guestCode || code : guestCode,
      );
      if (next.href !== current.href)
        window.history.replaceState(
          null,
          "",
          next.pathname + next.search + next.hash,
        );
    };
    const changedUser = Boolean(
      previousUser.current && previousUser.current !== userId,
    );
    if (changedUser) {
      if (ownCode.current) retiredCodes.current.add(ownCode.current);
      ownCode.current = null;
      rewrite(null);
    }
    previousUser.current = userId;
    if (!userId) {
      rewrite(null);
      return;
    }
    const update = () => {
      void fetch("/api/analytics/referral", {
        cache: "no-store",
        signal: AbortSignal.timeout(4000),
      })
        .then(async (response) => {
          if (!response.ok) return;
          const data = await response.json();
          if (cancelled) return;
          const previousCode = ownCode.current;
          ownCode.current =
            typeof data.code === "string" && REFERRAL_PATTERN.test(data.code)
              ? data.code
              : null;
          if (previousCode && previousCode !== ownCode.current)
            retiredCodes.current.add(previousCode);
          rewrite(ownCode.current);
        })
        .catch(() => undefined);
    };
    if (ownCode.current) rewrite(ownCode.current);
    else update();
    window.addEventListener("farbin:referral-changed", update);
    return () => {
      cancelled = true;
      window.removeEventListener("farbin:referral-changed", update);
    };
  }, [pathname, search, session?.user?.id, status]);
  useEffect(() => {
    const restored = (event: PageTransitionEvent) => {
      if (!event.persisted || !publicPage(window.location.pathname)) return;
      release.current = browserTracking().openPage(
        window.location.pathname + window.location.search,
        "history",
        null,
        true,
      );
    };
    window.addEventListener("pageshow", restored);
    return () => window.removeEventListener("pageshow", restored);
  }, []);
  return null;
}
